import { HireItem } from '../../../mocks/hireEquipment';
import { ClientInfo } from './ClientForm';

interface InvoiceLineItem {
  lineId: string;
  item: HireItem;
  qty: number;
  days: number;
}

interface InvoicePreviewProps {
  lineItems: InvoiceLineItem[];
  clientInfo: ClientInfo;
  invoiceNumber: string;
  deliveryFee: number;
  setupFee: number;
  discount: number;
  securityBond: number;
  signatureData?: {
    accepted: boolean;
    signedName: string;
    signedDate: string;
  };
  paymentStatus?: 'unpaid' | 'deposit_paid' | 'fully_paid';
}

export default function InvoicePreview({
  lineItems,
  clientInfo,
  invoiceNumber,
  deliveryFee,
  setupFee,
  discount,
  securityBond,
  signatureData,
  paymentStatus = 'unpaid',
}: InvoicePreviewProps) {
  const subtotal = lineItems.reduce((sum, li) => sum + li.item.price * li.qty * li.days, 0);
  const addOnsTotal = deliveryFee + setupFee;
  const discountAmount = (subtotal + addOnsTotal) * (discount / 100);
  const totalBeforeBond = subtotal + addOnsTotal - discountAmount;
  const total = totalBeforeBond + securityBond;

  const depositAmount = totalBeforeBond * 0.35;
  const balanceDue = totalBeforeBond - depositAmount;

  const today = new Date();
  const dueDate = new Date(today);
  dueDate.setDate(dueDate.getDate() + 14);

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

  const uniqueCategories = [...new Set(lineItems.map((li) => li.item.category))];

  return (
    <div id="invoice-preview" className="bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden invoice-a4">
      {/* Gold Header Bar */}
      <div className="bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 h-1.5 print-h-1"></div>

      <div className="p-5 sm:p-6 invoice-content">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-5">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h2 className="text-base font-display font-semibold text-stone-900">House of FMR</h2>
              <p className="text-amber-600 text-[9px] tracking-[0.2em] uppercase font-medium">Events &amp; Equipment Hire</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-display font-bold text-stone-900 tracking-tight">INVOICE</p>
            <p className="text-xs text-stone-500 mt-0.5">#{invoiceNumber}</p>
          </div>
        </div>

        {/* Payment Status Tracker */}
        <div className="mb-5 invoice-page-break">
          <div className="relative flex items-center justify-between">
            {/* Progress Line Background */}
            <div className="absolute top-4 left-[25%] right-[25%] h-0.5 bg-stone-200"></div>
            {/* Progress Line Fill */}
            <div
              className={`absolute top-4 left-[25%] h-0.5 transition-all ${
                paymentStatus === 'fully_paid'
                  ? 'bg-gradient-to-r from-amber-400 to-emerald-400 w-[50%]'
                  : paymentStatus === 'deposit_paid'
                  ? 'w-0'
                  : 'w-0'
              }`}
            ></div>

            {/* Step 1: Deposit Paid */}
            <div className="flex flex-col items-center relative z-10 flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                  paymentStatus === 'deposit_paid'
                    ? 'bg-amber-50 border-amber-400 text-amber-500'
                    : paymentStatus === 'fully_paid'
                    ? 'bg-stone-100 border-stone-300 text-stone-400'
                    : 'bg-white border-stone-200 text-stone-300'
                }`}
              >
                {paymentStatus === 'deposit_paid' ? (
                  <i className="ri-coin-line text-xs"></i>
                ) : paymentStatus === 'fully_paid' ? (
                  <i className="ri-check-line text-xs"></i>
                ) : (
                  <i className="ri-coin-line text-xs"></i>
                )}
              </div>
              <p className={`text-[8px] font-semibold mt-1 ${paymentStatus === 'deposit_paid' ? 'text-amber-600' : paymentStatus === 'fully_paid' ? 'text-stone-400' : 'text-stone-300'}`}>
                DEPOSIT PAID
              </p>
            </div>

            {/* Step 2: Fully Paid */}
            <div className="flex flex-col items-center relative z-10 flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                  paymentStatus === 'fully_paid'
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-500'
                    : 'bg-white border-stone-200 text-stone-300'
                }`}
              >
                {paymentStatus === 'fully_paid' ? (
                  <i className="ri-checkbox-circle-line text-xs"></i>
                ) : (
                  <i className="ri-checkbox-circle-line text-xs"></i>
                )}
              </div>
              <p className={`text-[8px] font-semibold mt-1 ${paymentStatus === 'fully_paid' ? 'text-emerald-600' : 'text-stone-300'}`}>
                FULLY PAID
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center mt-2.5">
            {paymentStatus === 'deposit_paid' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 text-[9px] font-bold rounded-full border border-amber-200">
                <i className="ri-coin-line text-[10px]"></i>DEPOSIT RECEIVED &mdash; BALANCE DUE
              </span>
            )}
            {paymentStatus === 'fully_paid' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-600 text-[9px] font-bold rounded-full border border-emerald-200">
                <i className="ri-checkbox-circle-line text-[10px]"></i>PAID IN FULL
              </span>
            )}
          </div>
        </div>

        {/* Dates & Info */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 pb-4 border-b border-stone-200">
          <div>
            <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-0.5">Issue Date</p>
            <p className="text-xs text-stone-800 font-medium">{formatDate(today)}</p>
          </div>
          <div>
            <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-0.5">Due Date</p>
            <p className="text-xs text-stone-800 font-medium">{formatDate(dueDate)}</p>
          </div>
          {clientInfo.eventDate && (
            <div>
              <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-0.5">Event Date</p>
              <p className="text-xs text-stone-800 font-medium">
                {new Date(clientInfo.eventDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>

        {/* From / To */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
          <div>
            <p className="text-[9px] text-amber-600 uppercase tracking-wider font-bold mb-1">From</p>
            <p className="text-xs font-semibold text-stone-900">House of FMR</p>
            <p className="text-[11px] text-stone-500 leading-relaxed">
              Melbourne, VIC, Australia<br />
              hello@houseoffmr.com<br />
              0432 730 295<br />
              ABN: 54 959693 565
            </p>
          </div>
          <div>
            <p className="text-[9px] text-amber-600 uppercase tracking-wider font-bold mb-1">Bill To</p>
            <p className="text-xs font-semibold text-stone-900">{clientInfo.clientName || 'Client Name'}</p>
            <p className="text-[11px] text-stone-500 leading-relaxed">
              {clientInfo.clientAddress || 'Client Address'}<br />
              {clientInfo.clientEmail || 'client@email.com'}<br />
              {clientInfo.clientPhone || 'Phone'}
            </p>
          </div>
        </div>

        {clientInfo.eventType && (
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full border border-amber-200">
              <i className="ri-calendar-event-line mr-1"></i>{clientInfo.eventType}
            </span>
            {clientInfo.eventVenue && (
              <span className="px-2.5 py-0.5 bg-stone-50 text-stone-600 text-[10px] font-medium rounded-full border border-stone-200">
                <i className="ri-map-pin-line mr-1"></i>{clientInfo.eventVenue}
              </span>
            )}
          </div>
        )}

        {/* Line Items Table */}
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-stone-900 text-white">
                <th className="text-left py-2 px-3 rounded-tl-lg font-medium text-[10px]">Item</th>
                <th className="text-center py-2 px-2 font-medium text-[10px]">Qty</th>
                <th className="text-center py-2 px-2 font-medium text-[10px]">Rate</th>
                <th className="text-center py-2 px-2 font-medium text-[10px]">Days</th>
                <th className="text-right py-2 px-3 rounded-tr-lg font-medium text-[10px]">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-stone-400 text-xs">
                    <i className="ri-file-list-3-line text-xl mb-1.5 block"></i>
                    Add equipment to see line items
                  </td>
                </tr>
              ) : (
                lineItems.map((li, idx) => (
                  <tr key={li.lineId} className={idx % 2 === 0 ? 'bg-stone-50/50' : 'bg-white'}>
                    <td className="py-1.5 px-3">
                      <div className="flex items-center gap-1">
                        <p className="font-medium text-stone-800 text-[11px]">{li.item.name}</p>
                        {li.item.promo && (
                          <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded">PROMO</span>
                        )}
                      </div>
                      <p className="text-[9px] text-stone-400">{li.item.category}</p>
                    </td>
                    <td className="text-center py-1.5 px-2 text-stone-700 text-[11px]">{li.qty}</td>
                    <td className="text-center py-1.5 px-2 text-stone-700 text-[11px]">${li.item.price.toFixed(2)}</td>
                    <td className="text-center py-1.5 px-2 text-stone-700 text-[11px]">{li.days}</td>
                    <td className="text-right py-1.5 px-3 font-semibold text-stone-900 text-[11px]">
                      ${(li.item.price * li.qty * li.days).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        {lineItems.length > 0 && (
          <div className="flex justify-end mb-5">
            <div className="w-full sm:w-72 space-y-1.5">
              <div className="flex justify-between text-[11px] text-stone-600">
                <span>Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between text-[11px] text-stone-600">
                  <span>Delivery &amp; Collection</span>
                  <span className="font-medium">${deliveryFee.toFixed(2)}</span>
                </div>
              )}
              {setupFee > 0 && (
                <div className="flex justify-between text-[11px] text-stone-600">
                  <span>Setup &amp; Pack Down</span>
                  <span className="font-medium">${setupFee.toFixed(2)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between text-[11px] text-amber-600">
                  <span>Discount ({discount}%)</span>
                  <span className="font-medium">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="pt-2 border-t border-stone-300 flex justify-between">
                <span className="text-xs font-semibold text-stone-800">Hire Total</span>
                <span className="text-xs font-bold text-stone-800">${totalBeforeBond.toFixed(2)}</span>
              </div>

              {/* Security Bond */}
              <div className="pt-2 mt-0.5 border-t border-dashed border-amber-400">
                <div className="flex justify-between text-[11px]">
                  <span className="text-amber-700 font-medium flex items-center gap-1">
                    <i className="ri-shield-check-line text-[10px]"></i>Security Bond
                  </span>
                  <span className="text-amber-700 font-semibold">${securityBond.toFixed(2)}</span>
                </div>
                <p className="text-[8px] text-amber-500 mt-0.5">
                  {uniqueCategories.length} categor{uniqueCategories.length === 1 ? 'y' : 'ies'} &times; $100 (max $500) &mdash; Refundable
                </p>
              </div>

              <div className="pt-2 border-t-2 border-stone-900 flex justify-between">
                <span className="text-sm font-bold text-stone-900">Total Due (AUD)</span>
                <span className="text-base font-display font-bold text-stone-900">${total.toFixed(2)}</span>
              </div>

              {/* Deposit & Balance - compact inline */}
              <div className="pt-1.5 flex justify-between text-[10px] text-stone-500">
                <span>Deposit (35%): <strong className="text-stone-700">${depositAmount.toFixed(2)}</strong></span>
                <span>Balance: <strong className="text-stone-700">${balanceDue.toFixed(2)}</strong></span>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {clientInfo.notes && (
          <div className="mb-4 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
            <p className="text-[9px] text-amber-600 uppercase tracking-wider font-bold mb-0.5">Notes</p>
            <p className="text-[11px] text-stone-600 leading-relaxed">{clientInfo.notes}</p>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="pt-4 border-t-2 border-stone-300">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-file-text-line text-amber-600 text-sm"></i>
            <p className="text-[10px] text-stone-800 uppercase tracking-wider font-bold">Terms &amp; Conditions</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {/* Payment Terms */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-bank-card-line text-emerald-600 text-[10px]"></i>Payment Terms
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; 35% deposit required within 48 hours of acceptance</li>
                <li>&#8226; Balance (65%) due 7 days prior to event</li>
                <li>&#8226; Security bond payable separately before delivery</li>
                <li>&#8226; Late payments may result in booking cancellation</li>
              </ul>
            </div>

            {/* Security Bond */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-shield-check-line text-amber-600 text-[10px]"></i>Security Bond
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; $100 per category (capped at $500 max)</li>
                <li>&#8226; Refundable within 5 business days on safe return</li>
                <li>&#8226; Damage/loss deducted from bond</li>
                <li>&#8226; Consumables &amp; Staffing excluded from bond</li>
              </ul>
            </div>

            {/* Delivery & Collection */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-truck-line text-blue-600 text-[10px]"></i>Delivery &amp; Collection
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; Available within Melbourne metro area</li>
                <li>&#8226; Delivered on event day or day prior</li>
                <li>&#8226; Collection within 24 hours after event</li>
                <li>&#8226; Client responsible for equipment security</li>
              </ul>
            </div>

            {/* Cancellation Policy */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-close-circle-line text-rose-600 text-[10px]"></i>Cancellation Policy
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; 14+ days before: 50% deposit refund</li>
                <li>&#8226; 7-14 days before: 25% deposit refund</li>
                <li>&#8226; Less than 7 days: No refund</li>
                <li>&#8226; Date changes subject to availability</li>
              </ul>
            </div>

            {/* Liability */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-alert-line text-orange-600 text-[10px]"></i>Liability &amp; Damage
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; Client assumes full responsibility during hire</li>
                <li>&#8226; Damage/loss charged at replacement value</li>
                <li>&#8226; Must be used per provided instructions</li>
                <li>&#8226; Report any damage immediately</li>
              </ul>
            </div>

            {/* Age Restriction */}
            <div>
              <p className="text-[10px] font-semibold text-stone-800 mb-1 flex items-center gap-1">
                <i className="ri-user-forbid-line text-purple-600 text-[10px]"></i>Age Restriction
              </p>
              <ul className="text-[9px] text-stone-600 space-y-0.5 leading-relaxed ml-3">
                <li>&#8226; Bookings accepted for clients 18+ only</li>
                <li>&#8226; Photo ID may be required at delivery</li>
                <li>&#8226; RSA compliance applies to all bar services</li>
                <li>&#8226; All alcohol consumers must be 18+</li>
              </ul>
            </div>
          </div>

          <div className="mt-3 p-2.5 bg-stone-50 rounded-lg border border-stone-200">
            <p className="text-[9px] text-stone-600 leading-relaxed">
              By accepting this invoice, you agree to these terms and conditions. For questions, contact us at <strong>hello@houseoffmr.com</strong> or call <strong>0432 730 295</strong>.
            </p>
          </div>

          <p className="text-[10px] text-amber-600 italic mt-3 text-center font-medium">Thank you for choosing House of FMR</p>
        </div>

        {/* Client Acceptance & Signature */}
        <div className="mt-5 pt-4 border-t-2 border-stone-300">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-quill-pen-line text-amber-600 text-sm"></i>
            <p className="text-[10px] text-stone-800 uppercase tracking-wider font-bold">Client Acceptance &amp; Signature</p>
          </div>

          <div className="p-3.5 bg-stone-50 rounded-xl border border-stone-200 mb-4">
            <p className="text-[9px] text-stone-600 leading-relaxed mb-3">
              I, the undersigned, confirm that I have read, understood, and agree to all terms and conditions outlined in this invoice. 
              I authorise House of FMR to proceed with the equipment hire and/or staffing services as detailed above. 
              I acknowledge the payment schedule, security bond requirements, and cancellation policy.
            </p>
            <div className="flex items-start gap-2">
              <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5">
                {signatureData?.accepted ? (
                  <i className="ri-checkbox-fill text-amber-600 text-sm"></i>
                ) : (
                  <div className="w-3 h-3 border-2 border-stone-300 rounded-sm"></div>
                )}
              </div>
              <p className="text-[9px] text-stone-700 font-medium leading-relaxed">
                I agree to the Terms &amp; Conditions and authorise this booking with House of FMR Events &amp; Equipment Hire.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Signature Line */}
            <div>
              <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-1.5">Client Signature</p>
              <div className="h-14 border-b-2 border-stone-400 relative flex items-end pb-1">
                {signatureData?.signedName ? (
                  <p className="text-base font-display italic text-stone-700 pl-1">{signatureData.signedName}</p>
                ) : (
                  <p className="text-[10px] text-stone-300 italic pl-1">Awaiting signature...</p>
                )}
              </div>
            </div>

            {/* Date */}
            <div>
              <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-1.5">Date</p>
              <div className="h-14 border-b-2 border-stone-400 relative flex items-end pb-1">
                {signatureData?.signedDate ? (
                  <p className="text-xs text-stone-700 font-medium pl-1">{signatureData.signedDate}</p>
                ) : (
                  <p className="text-[10px] text-stone-300 italic pl-1">DD / MM / YYYY</p>
                )}
              </div>
            </div>
          </div>

          {/* Printed Name */}
          <div className="mt-4">
            <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-1.5">Printed Name</p>
            <div className="h-8 border-b-2 border-stone-400 flex items-end pb-1">
              {signatureData?.signedName ? (
                <p className="text-xs text-stone-800 font-semibold pl-1">{signatureData.signedName}</p>
              ) : (
                <p className="text-[10px] text-stone-300 italic pl-1">Full name of authorised person</p>
              )}
            </div>
          </div>

          {/* House of FMR Counter-Signature */}
          <div className="mt-5 pt-4 border-t border-dashed border-stone-300">
            <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-3">For Office Use Only</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-1.5">Authorised by House of FMR</p>
                <div className="h-12 border-b-2 border-stone-300 flex items-end pb-1">
                  <p className="text-[10px] text-stone-300 italic pl-1">Signature</p>
                </div>
              </div>
              <div>
                <p className="text-[9px] text-stone-400 uppercase tracking-wider font-semibold mb-1.5">Date</p>
                <div className="h-12 border-b-2 border-stone-300 flex items-end pb-1">
                  <p className="text-[10px] text-stone-300 italic pl-1">DD / MM / YYYY</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
